import { hyphenate } from '../lib/utils';

export const initTablesOfContents = () => {
	const els = document.querySelectorAll('.js-table-of-contents');

	for (const el of els) {
		createTableOfContents(el);
	}
};

/**
 * A table entry.
 * @typedef {{
*   li: HTMLElement;
*   a: HTMLAnchorElement;
* }} TableEntry
*/

/**
* A content item with the table entry and the header element.
* @typedef {{
*   entry: TableEntry;
*   headingEl: HTMLElement;
* }} Item
*/

/**
* An item alongside a click event listener for the entry link.
* @typedef {{
*   item: Item;
*   handler: EventListener;
* }} LinkListener
*/

/**
 * Initializes a table of contents component.
 * @param {HTMLElement} el - Table of contents element.
 */
const createTableOfContents = (el) => {
	const
		targetEl = el.closest('.js-table-of-contents-target'),
		entriesEl = el.querySelector('.js-table-of-contents-entries');

	if (
		!targetEl
		|| !entriesEl
	) {
		throw new Error('Missing table of contents elements');
	}

	const headingEls = Array
		.from(targetEl.querySelectorAll('h2:not([data-toc-skip])'));

	if (!headingEls.length) {
		console.warn('Table of contents has no entries, keeping element hidden');
		return;
	}

	el.style.display = '';

	/**
	 * All content items.
	 * @type {Item[]}
	 */
	const items = getContentItems(
		entriesEl,
		headingEls
	);

	/**
	 * The active content item.
	 * @type {Item | null}
	 */
	let activeItem = null;

	/**
	 * Sets the active content item.
	 * @param {Item | null} item
	 */
	const setActiveItem = (item) => {
		if (item === activeItem) {
			return;
		}

		if (activeItem) {
			activeItem.entry.li.dataset.tocEntryActive = 'false';
			activeItem.entry.li.dataset.menuPositionBarActive = 'false';
		}

		if (item) {
			item.entry.li.dataset.tocEntryActive = 'true';
			item.entry.li.dataset.menuPositionBarActive = 'true';
		}

		// use default anchor behavior instead
		// item.headingEl.scrollIntoView(true);

		activeItem = item;
	};

	const
		observer = createHeadingObserver(items, setActiveItem),
		listeners = createLinkListeners(items, setActiveItem);

	/**
	 * Attaches event listeners and observers to the content items.
	 */
	const listen = () => {
		for (const item of items) {
			observer.observe(item.headingEl);
		}

		for (const listener of listeners) {
			listener.item.entry.a.addEventListener('click', listener.handler);
		}
	};

	setActiveItem(items[0] ?? null);

	listen();
};

/**
 * Creates and returns the items for/within a table of contents.
 * @param {HTMLElement} entriesEl - Table entries element.
 * @param {HTMLElement} headingEls - Heading elements.
 * @returns {Item[]} Content items.
 */
const getContentItems = (entriesEl, headingEls) => {
	return headingEls.map((headingEl) => {
		const entry = createEntry(headingEl);
		entriesEl.append(entry.li);
		return { entry, headingEl };
	});
};

/**
 * Creates and returns a table entry with a `<li>` and `<a>` element.
 * @param {HTMLElement} headingEl - Heading element.
 * @returns {TableEntry} Table entry.
 */
const createEntry = (headingEl) => {
	headingEl.id = hyphenate(headingEl.textContent);

	const li = document.createElement('li');
	li.classList.add(
		'table-of-contents__entry',
		'menu-position-bar__item'
	);

	const a = document.createElement('a');
	a.href = `#${headingEl.id}`;
	a.textContent = headingEl.dataset.shortTitle || headingEl.textContent;
	a.classList.add(
		'table-of-contents__link',
		'menu-position-bar__link'
	);

	li.append(a);
	li.dataset.tocEntryActive = 'false';
	li.dataset.menuPositionBarActive = 'false';

	return { li, a };
};

/**
 * Creates an IntersectionObserver to be used for updating the active content item depending on
 * which heading is intersecting the viewport.
 * @param {Item[]} items - Content items.
 * @param {(item: Item) => void} callback - Callback.
 * @returns {IntersectionObserver} Intersection Observer.
 */
const createHeadingObserver = (items, callback) => {
	return new IntersectionObserver((obsEntries) => {
		const item = tryGetInViewItem(items, obsEntries);

		if (!item) {
			return;
		}

		callback(item);
	}, {
		// offset of 10px to accommodate for scrollIntoView not exactly
		// lining up the scroll target with the top of the viewport.
		rootMargin: `-10px 0px 0px 0px`,
		threshold: 1
	});
};

/**
 * Attempts to return the in-view content item from a set of IntersectionObserver entries, or null
 * if it it couldn't be determined.
 * @param {Item[]} items - Content items.
 * @param {IntersectionObserverEntry[]} obsEntries - IntersectionObserver entries.
 * @returns {Item | null} In-view content item, or null.
 */
const tryGetInViewItem = (items, obsEntries) => {

	// search through the items/headers sequentially, not the entries

	for (const item of items) {
		const obsEntry = obsEntries
			.find((obsEntry) => obsEntry.target === item.headingEl);

		if (
			!obsEntry
			// if the header is leaving/entering the bottom of the screen (note: don't
			// use entry.intersectionRect.height, sometimes the callback is fired a
			// bit late and the top pos is greater than the height)
			|| obsEntry.boundingClientRect.top > innerHeight / 2
		) {
			continue;
		}

		const inViewItem = obsEntry.isIntersecting
			// header is entering the top of the screen
			? items[items.indexOf(item) - 1] ?? item
			// header is leaving the top of the screen
			: item;

		return inViewItem;
	}

	return null;
};

/**
 * Creates and returns an array of click listeners to each table entry link to set the corresponding
 * item as the active item.
 * @param {Item[]} items - Content items.
 * @param {(item: Item) => void} callback - Callback function.
 * @returns {LinkListener[]} Entry link listeners.
 */
const createLinkListeners = (items, callback) => {
	if (!('onscrollend' in window)) {
		return [];
	}

	return items.map((item) => {
		const handler = () => {
			let scrolled = false;

			const handleScrollEnd = () => {
				// this creates a race between intersection observer firing
				// and scrollend event firing, but this should always lose
				// because of our intersection offset (which we want).
				callback(item);
				scrolled = false;
			};

			const handleScroll = () => {
				scrolled = true;
				addEventListener('scrollend', handleScrollEnd, { once: true });
			};

			addEventListener('scroll', handleScroll, { passive: true, once: true });

			setTimeout(() => {
				if (scrolled) {
					return;
				}

				removeEventListener('scroll', handleScroll);
				callback(item);
			}, 50);
		};

		return { item, handler };
	});
};
